import Modal from './resourceManagement.modal.component';
import { FormioUtils } from '@formio/angular';
import { EventEmitter } from '@angular/core';
import template from './resourcesManagement.component.html';
import _ from 'lodash';
import { TypeCoerce } from '../../base/TypeCoerce.js';

const SHORT_DATE = 'MM/dd/yyyy';

/**
 * @typedef {object} Type
 * @property {string} name
 * @property {string} description
 * @property {object} formSchema
 * @property {string} [model]
 * @property {string[]} groups
 * @property {object} data
 */

/**
 * @typedef {object} ResourceList
 * @property {string} name
 * @property {string} type
 * @property {org} org
 * @property {string[]} groups
 */

/**
 * @typedef {object} Resource
 * @property {string} type
 * @property {string} resourcelist
 * @property {string} data
 */


class ResourcesManagement {
  constructor(
    $scope,
    $state,
    $stateParams,
    $transitions,
    growl,
    appConfig,
    ModalService,
    TypeService,
    ResourceService,
    ResourceListService,
    Organization
  ) {
    'ngInject';

    this.$scope = $scope;
    this.$state = $state;
    this.growl = growl;
    this.$stateParams = $stateParams;
    this.skip = 0;
    this.limit = 10;
    this.filter = {};
    this.service = {
      Type: TypeService,
      Resource: ResourceService,
      ResourceList: ResourceListService,
      Modal: ModalService,
      Organization
    };
    this.appConfig = appConfig;
    this.resources = [];
    this.types = [];
    this.selectedResourceListType = {};
    this._pendingTypesFetch = null;

    this.hasOwnerOrg = false;

    this.resourcesOptions = {};

    this.resourcesArr = [];
    this.resourcesCount = 0;
    this.values = [];
    this._resourceLists_ = new Map();
    this.triggerClear = new EventEmitter();

    // Nested route handling
    this.transitionHooks = [
      $transitions.onSuccess({ to: 'admin.resources.resource' }, () => {
        this.selected = this.$stateParams.resourceId;
        this.selectedResourceListType = this.types[this.selected.type];
        this.hasOwnerOrg = Boolean(_.get(this.orgs, ['byId', this.selected.org], false));
        this.updateOptions();
        // clear table filters and sort, and trigger lazyLoadEvent to retrieve data
        this.triggerClear.emit();
      }),
      $transitions.onSuccess({ to: 'admin.resources.new' }, () => this.newResourceList()),
      $transitions.onSuccess({ to: 'admin.resources.resource.edit' }, () => this.editResourceList()),
    ];
  }

  async $onInit() {
    await this.fetchResourceLists();

    // If route has a resourceId, set it as selected
    this.selected = this.$stateParams.resourceId;

    await this.fetchTypes();

    this.orgs = await this.service.Organization.model;
    if (this.selected) {
      this.hasOwnerOrg = Boolean(_.get(this.orgs, ['byId', this.selected.org], false));
      this.updateOptions();
    }

    switch (this.$state.current.name) {
      case 'admin.resources.resource':
      // this.fetchResources();
        break;
      case 'admin.resources.new':
        this.newResourceList();
        break;
      case 'admin.resources.resource.edit':
        this.editResourceList();
        break;
      default:
    }
  }

  fetchResourceLists() {
    return this.service.ResourceList
      .getResourceLists()
      .then(({ data: resources }) => { this.addResourceList(...resources) });
  }

  fetchTypes() {
    this._pendingTypesFetch = this.service.Type
      .getAllTypes()
      .then(({ data: types }) => {
        this.types = _.keyBy(types, 'id');
        // get types storageType
        this.schemas = _.reduce(types, (acc, type) => {
          const schemaTemp = type.formSchema?.components?.[0].components;
          if (schemaTemp) {
            acc[type.id] = _.reduce(schemaTemp, (schemaAcc, field) => {
              schemaAcc[field.key] = field?.properties?.storageType
                ? field.properties : { storageType: 'string' };
              return schemaAcc;
            }, {});
          }
          return acc;
        }, {});
        if (this.selected) {
          this.selectedResourceListType = this.types[this.selected.type];
          this.updateOptions();
        }
      });
    return this._pendingTypesFetch;
  }


  /**
   * @readonly
   * @returns {Resource[]} A list all resources
   */
  get resourceLists() {
    return [...this._resourceLists_.values()];
  }

  /**
   * DON'T ASSIGN THIS; use openResource modal instead
   *
   * @private
   * @param {string} resourceId ID of type to select
   */
  set selected(resourceId) {
    this._selected_ = resourceId;
  }

  /**
   * @returns {Resource|undefined} The currently selected Resource, if any
   */
  get selected() {
    return this._resourceLists_.get(this._selected_);
  }

  /**
   * Opens the type page for a given resourceId
   *
   * @param {string} [resourceId] Type to open; defaults to selected type
   */
  openResource(resourceId = this._selected_) {
    this.$state.go('admin.resources.resource', { resourceId });
  }

  /**
   * Opens the resources page
   */
  openResources() {
    this.$state.go('admin.resources');
  }

  static getComponentDataType(type) {
    let result;
    switch (_.lowerCase(type)) {
      case 'numeric':
      case 'number':
      case 'integer':
      case 'int':
      case 'float':
        result = 'numeric';
        break;
      case 'checkbox':
        result = 'boolean';
        break;
      case 'datetime':
      case 'day':
      case 'date':
      case 'time':
        result = 'date';
        break;
      default:
        result = 'text';
        break;
    }
    return result;
  }

  /**
   * Populates resources for a resourceList and determines the available columns for the table
   *
   * @param {resourceList} [resourceList] ResourceList to fetch resources for, defaults to selected
   * @returns {undefined}
   */
  async fetchResources(resourceList = this.selected) {
    this.filter.resourcelist = resourceList.id;
    try {
      ({ data: { resources: this.resources, count: this.resourcesCount } } =
        await this.service.ResourceList.getResourcesForList(
          resourceList.id, this.limit, this.skip, this.sort, this.filter));

      // Wait for types to be fetched before mapping columns
      await this._pendingTypesFetch;

      const mappedColumns = [..._.reduce(
        FormioUtils.flattenComponents(this.selectedResourceListType?.formSchema?.components),
        (acc, component, key) => {
          if (!component.hidden) {
            const type = ResourcesManagement.getComponentDataType(component.type);
            const formatOptions = {};
            if (type === 'date') {
              formatOptions.format = component.format || SHORT_DATE;
              if (component.displayInTimezone === 'utc') {
                formatOptions.timezone = 'UTC';
              }
            }
            acc.set(key, {
              field: key,
              header: component.label,
              type,
              ...!_.isEmpty(formatOptions) && { formatOptions },
            });

            // don't show containers/objects, just the fields within
            const parts = key.split('.');
            if (parts.length > 1) {
              let popped = parts.pop();
              while (popped) {
                const parent = parts.join('.');
                if (acc.has(parent)) {
                  acc.delete(parent);
                }
                popped = parts.pop();
              }
            }
          }
          return acc;
        },
        new Map()).values()];
      // Add createdAt & updatedAt since they are not in form schema prop.
      mappedColumns.push(
        { field: 'createdAt', header: 'Created At', type: 'date', formatOptions: { format: SHORT_DATE } },
        { field: 'updatedAt', header: 'Updated At', type: 'date', formatOptions: { format: SHORT_DATE } }
      );

      // Need to check emptiness because user may have filtered a column whose query returns no resources
      if (!(_.isEmpty(mappedColumns) || _.isEqual(this.columns, mappedColumns))) {
        this.columns = mappedColumns;
      }

      this.values = _.map(this.resources, ({ id, type, data, createdAt, updatedAt }) =>
        ({ id, type, data: this.formatData(type, data), createdAt, updatedAt }));
      this.$scope.$apply();
    } catch (err) {
      console.error(err);
      this.growl.error('Unable to retrieve resources. Try again');
    }
  }

  formatData(type, data) {
    const schema = this.schemas[type];

    return _.reduce(data, (acc, value, key) => {
      try {
        const storageType = schema?.[key]?.storageType;
        if (storageType === 'number') {
          value = TypeCoerce.number.to(value);
        } else if (storageType === 'date' || storageType === 'datetime') {
          value = TypeCoerce.date.to(value, schema?.[key]?.timezone);
        }
      } catch {
        _.noop();
      }
      acc[key] = value;
      return acc;
    }, {});
  }

  /**
   * Adds a resourcelist to the view modal
   * Don't call this; use the modal instead
   *
   * @private
   * @param {...ResourceList} resourceLists ResourceList(s) to add
   * @returns {undefined}
   */
  addResourceList(...resourceLists) {
    resourceLists.forEach(resourceList => this._resourceLists_.set(resourceList.id, resourceList));
    this.resourcesArr = _.sortBy(this.resourceLists, res => res.name.toLowerCase());
    this.$scope.$digest();
  }

  /**
   * Opens the modal for editing a resource list and submits changes
   *
   * @param {string} [resourceListId] Target - If omitted, targets selected type
   * @returns {Promise<undefined>} Result of modal
   */
  editResourceList(resourceListId) {
    if (!resourceListId) {
      try {
        // Default to selected resource list ID, if any
        resourceListId = this.selected.id;
      } catch (err) {
        // This should never happen
        console.error('tried to edit without specifying resource list id', err);
      }
    }
    return this
      .openModal(resourceListId)
      .then(async (updatedResourceList) => {
        try {
          const { data: [resourceList] } = await this.service.ResourceList.update(resourceListId, updatedResourceList);
          const { groups } = updatedResourceList;
          this.addResourceList({ ...resourceList, groups });
          this.growl.success(`Updated ${updatedResourceList.name}`);
        } catch (err) {
          this.growl.error(err.error || err, { title: `Error updating ${updatedResourceList.name}` });
        }
      })
      .catch(_.noop) // User closed modal
      .finally(() => this.openResource(resourceListId));
  }

  /**
   * Opens the modal for creating a new resource list and submits it
   *
   * @returns {Promise<undefined>} Result of opening modal
   */
  newResourceList() {
    return this
      .openModal()
      .then(async (newResourceList) => {
        try {
          const { data: resourcelist } = await this.service.ResourceList.create('', newResourceList);
          const { groups } = newResourceList;
          this.addResourceList({ ...resourcelist, groups });
          this.growl.success(resourcelist.name, { title: 'Created Resource List' });
          this.openResource(resourcelist.id);
        } catch (err) {
          this.growl.error(err.error || err, { title: `Unable to create ${newResourceList.name}` });
        }
      })
      .catch(() => this.selected ? this.openResource() : this.$state.go('admin.resources'));
  }

  /**
   * Opens the modal for deleting a resource and submits its request
   *
   * @param {string} resourceId ID of resource. Defaults to selected resource, if omitted
   * @returns {Promise<undefined>} Result of opening confirmation modal
   * @throws If target resource is not specified
   */
  destroyResourceList(resourceId = this.selected.id) {
    if (!resourceId) {
      throw new TypeError('Tried to destroy without specifying or selecting target');
    }
    const { name } = this._resourceLists_.get(resourceId);
    return this.service.Modal
      .confirmationModal({
        modalTitle: 'Delete Resource List',
        optionalWarning: 'This will delete the resource list and all of its resources from the platform.',
        selectionCriteria: `The resource list ${name} will be removed.`,
      })
      .then(async () => {
        try {
          await this.service.ResourceList.destroy(resourceId);
          this._resourceLists_.delete(resourceId);
          this.growl.success(`Deleted ${name}`);
          await this.fetchResourceLists();
          this.openResources();
        } catch (err) {
          this.growl.error(err.error || err, { title: `Unable to delete ${name}` });
        }
      })
      .catch(_.noop); // User closed modal
  }

  openModal(resourceId) {
    return this.service.Modal.open({
      ...Modal,
      size: 'lg',
      resolve: {
        resourceList: _.cloneDeep(this._resourceLists_.get(resourceId))
      }
    });
  }

  $onDestroy() {
    this.transitionHooks.forEach(unregister => unregister()); // UI-router hack to prevent multiple modals
  }

  onImportSuccess(fileName) {
    this.fetchResources();
    this.growl.success(`File ${fileName} was imported successfuly`);
  }

  addResource(newResource) {
    newResource.type = this.selected.type;
    newResource.org = this.selected.org;
    this.service.Resource.create(this.selected.id, newResource)
      .then(() => {
        this.growl.success('New Resource Created');
      })
      .catch(err => this.growl.error(err.data.error, { title: 'Unable to add resource' }))
      .finally(() => this.fetchResources());
  }

  /**
   * Saves resource
   *
   * @param {string} selectedResource Resource
   * @returns {Promise<undefined>} Result of saving resource
   * @throws If target resource is not specified
   */
  saveResource(selectedResource) {
    if (!selectedResource) {
      throw new TypeError('Tried to save without specifying or selecting target');
    }
    const resource = this.resources.find(resItem => resItem.id === selectedResource.id);

    resource.data = selectedResource.data || {};

    const { data: { name = 'resource' } } = resource;
    return this.service.Resource.update(this.selected.id, resource.id, resource)
      .then(() => {
        resource.enableEdit = false;
        this.growl.success(`Saved ${name}`);
      })
      .catch(err => this.growl.error(err.data.error || err, { title: `Unable to save ${name}` }))
      .finally(() => this.fetchResources());
  }

  /**
   * Opens the modal for deleting a resource and submits its request
   *
   * @param {Resource} resource Resource
   * @returns {Promise<undefined>} Result of opening confirmation modal
   * @throws If target resource is not specified
   */
  destroyResource(resource) {
    if (!resource) {
      throw new TypeError('Tried to destroy without specifying or selecting target');
    }
    const { id, name = 'resource' } = resource;

    return this.service.Modal
      .confirmationModal({ modalTitle: `Delete ${name}?` })
      .then(() => this.service.Resource.destroy(this.selected.id, id))
      .catch(err => this.growl.error(err.error || err, { title: `Unable to delete ${name}` }))
      .finally(() => this.fetchResources());
  }


  handleExportClicked(listName) {
    this.service.ResourceList.getResourcesExport(this.selected.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' });
        const date = new Date();
        saveAs(
          blob,
          `${listName} ` +
          `(${date.getMonth() + 1}.${date.getDate()}.${date.getFullYear()}.${date.getHours()}.${date.getMinutes()}).csv`
        );
      });
  }

  updateOptions() {
    this.resourcesOptions = {
      showAdd: this.hasOwnerOrg,
      showExport: true,
      showColumnsMenu: true,
      actions: this.hasOwnerOrg,
      columnMenu: true,
      paginator: true,
      search: true,
      sortable: true,
      uploadUrl: `${this.appConfig.apiBaseUrl}/resourcelist/${this.selected.id}/resource/import/file`
    };
  }

  handleLazyLoad($event) {
    if ($event && $event.sortField) {
      this.sort = JSON.stringify({ [$event.sortField]: $event.sortOrder === -1 ? 'DESC' : 'ASC' });
    } else if ($event && _.isArray($event.multiSortMeta)) {
      const sortOptions = _.reduce(
        $event.multiSortMeta,
        (acc, { field, order }) => {
          acc[field] = order === -1 ? 'DESC' : 'ASC';
          return acc;
        },
        {});
      this.sort = JSON.stringify(sortOptions);
    }
    if ($event) {
      this.skip = $event.first;
    }
    if ($event && $event.rows) {
      this.limit = $event.rows;
    }
    if ($event?.filters) {
      this.processFilters($event.filters);
    }
    this.fetchResources();
  }

  processFilters(filters) {
    this.filter = _.reduce(filters, (acc, fieldFilters, field) => {
      const filterData = _.reduce(fieldFilters, (acc2, filter) => {
        if (filter.value) {
          acc2[filter.matchMode] = filter.value;
        }
        return acc2;
      }, {});

      if (!_.isEmpty(filterData)) {
        acc[field] = _.get(filterData, 'equals', filterData);
      }
      return acc;
    }, {});
  }
}

export default {
  name: 'resourceManagement',
  template,
  controller: ResourcesManagement,
  bindings: {}
};
